// The organizations list (spec/dashboard.md section 2.3): a row opens on a single click (`openOnClick`) and the name is a
// link to the organization's games, so Ctrl-click opens it in a new tab.
import { useCallback, useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiError } from '../api/client';
import type { Organization } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { EmptyState } from '../ui/Banner';
import { Button } from '../ui/Button';
import { DataGrid } from '../ui/DataGrid';
import { Field } from '../ui/Field';
import { Building2, Plus } from '../ui/icons';
import { TextInput } from '../ui/inputs';
import { formatInstantWithZone } from '../ui/time';
import { Modal } from '../ui/Modal';
import { LoadError, LoadingPane } from '../ui/PageStates';
import './org/org.css';

export function Organizations() {
  const { client } = useAuth();
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<Organization[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      setOrgs((await client.organizations()).items);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (request ${e.requestId})` : 'Failed to load.');
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section>
      <div className="page-head">
        <h1>Organizations</h1>
        {orgs && <span className="page-meta">{orgs.length}</span>}
        <div className="spacer" />
        <Button variant="primary" icon={<Plus aria-hidden="true" />} onClick={() => setCreating(true)}>
          New organization
        </Button>
      </div>
      {error ? (
        <LoadError message={error} />
      ) : !orgs ? (
        <LoadingPane />
      ) : (
        <DataGrid
          id="orgs"
          height={480}
          caption="Organizations you belong to"
          rows={orgs}
          rowKey={(o) => o.id}
          onRowOpen={(o) => navigate(`/orgs/${o.slug}/games`)}
          openOnClick
          emptyState={
            <EmptyState
              icon={<Building2 />}
              title="No organizations yet"
              body="You are not a member of any organization yet. Create one to get started."
              action={<Button onClick={() => setCreating(true)}>Create an organization</Button>}
            />
          }
          columns={[
            { key: 'name', header: 'Name', render: (o) => <Link to={`/orgs/${o.slug}/games`}>{o.name}</Link>, cellText: (o) => o.name, sortValue: (o) => o.name },
            { key: 'slug', header: 'Slug', render: (o) => <code>{o.slug}</code> },
            { key: 'created', header: 'Created', render: (o) => formatInstantWithZone(o.createdAt) },
          ]}
        />
      )}
      <CreateOrganizationModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(o) => {
          setCreating(false);
          navigate(`/orgs/${o.slug}/games`);
        }}
      />
    </section>
  );
}

function CreateOrganizationModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (o: Organization) => void }) {
  const { client } = useAuth();
  const formId = useId();
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (open) {
      setIdempotencyKey(crypto.randomUUID());
      setError(null);
    }
  }, [open]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onCreated(await client.createOrganization({ slug, name }, idempotencyKey));
      setSlug('');
      setName('');
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(0, 'internal_error', 'Something went wrong.', ''));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New organization"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} variant="primary" busy={busy}>
            Create
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit}>
        <Field label="Name" error={error?.fieldMessage('name')}>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Slug" help="Lowercase letters, digits and dashes." error={error?.fieldMessage('slug')}>
          <TextInput className="mono" value={slug} onChange={(e) => setSlug(e.target.value)} pattern="[a-z0-9][a-z0-9-]{1,62}" required />
        </Field>
        {error && !error.details?.length && <LoadError message={error.message} />}
        <p className="muted org-note">You become the owner. Games, environments and keys live under the organization.</p>
      </form>
    </Modal>
  );
}
